import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateDeliveryTime } from '../utils/timeUtils';

interface CountdownTimerProps {
  orderTime: string;
  estimatedDeliveryMinutes: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ orderTime, estimatedDeliveryMinutes }) => {
  const { t } = useTranslation('orders');
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isOverdue, setIsOverdue] = useState<boolean>(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const { remaining, isOverdue: overdue } = calculateDeliveryTime(orderTime, estimatedDeliveryMinutes);
      setTimeRemaining(remaining);
      setIsOverdue(overdue);
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [orderTime, estimatedDeliveryMinutes]);

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
      isOverdue 
        ? 'bg-red-50 text-red-700 border border-red-200' 
        : 'bg-orange-50 text-orange-700 border border-orange-200'
    }`}>
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>
        {t(isOverdue ? 'countdown.overdue' : 'countdown.dueIn', { time: formatTime(timeRemaining) })}
      </span>
    </div>
  );
};

export default CountdownTimer;
